import { Suspense, useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { replayPendingShots } from '../lib/queries/pendingShotsReplay';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import LinearProgress from '@mui/material/LinearProgress';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import NewReleasesOutlined from '@mui/icons-material/NewReleasesOutlined';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import SellOutlined from '@mui/icons-material/SellOutlined';
import CardMembershipOutlined from '@mui/icons-material/CardMembershipOutlined';
import InventoryOutlined from '@mui/icons-material/Inventory2Outlined';
import BuildOutlined from '@mui/icons-material/BuildOutlined';
import SportsOutlined from '@mui/icons-material/SportsOutlined';
import GpsFixedOutlined from '@mui/icons-material/GpsFixedOutlined';
import AdjustOutlined from '@mui/icons-material/AdjustOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import QSLogo from './QSLogo';
import { useTaxonomyOverridesVersion } from './TaxonomyOverridesProvider';
import Badge from '@mui/material/Badge';
import { useFeedback } from './useFeedback';
import { useInvite } from './useInvite';
import { useStaleBundle } from './useStaleBundle';
import { useAuth } from '../contexts/useAuth';

// Drawer widths. Desktop expanded sits at the Material baseline.
// Mobile drawer is narrower (240) because at 375px viewport, 256
// leaves only 119px of the page behind — uncomfortable. Collapsed
// "mini" form is sized so a 24px icon centers comfortably with a
// ~24px hit-area padding on either side. Anything below ~64px makes
// the icons feel cramped and the tooltips overlap the row.
const DRAWER_WIDTH_EXPANDED = 256;
const DRAWER_WIDTH_MOBILE = 240;
const DRAWER_WIDTH_COLLAPSED = 72;
const COLLAPSED_KEY = 'qs-drawer-collapsed';
// M54a — "Who I'm with" expander state. Default false (collapsed)
// for everyone. The community section (clubs / venues / events /
// sharing circles) isn't load-bearing for first-run, but it's also
// not a junk drawer — it has its own semantic. Existing users with
// muscle memory for Memberships / Venues / Competitions / Groups
// pay one extra click on first load; localStorage remembers their
// preference thereafter.
const COMMUNITY_OPEN_KEY = 'qs-drawer-community-open';

interface NavItem {
  label: string;
  /** Route to navigate to. Mutually exclusive with `onClick`. */
  path?: string;
  /** Custom action — e.g. opening the feedback dialog. Mutually
   * exclusive with `path`. Items with `onClick` are never marked
   * "selected" since they don't represent a destination. */
  onClick?: () => void;
  icon: React.ReactNode;
  /** Optional small text under the label (used to surface the version
   * on the "What's new" row). Hidden when the drawer is collapsed. */
  secondary?: string;
}

// M54a — Drawer reorg into 3 user-journey sections. Replaces the 6
// domain-labeled sections (Main / Finances / Inventory / Training /
// Competition / Collaboration) that overwhelmed first-time users
// with all 13 destinations at once. The mental model now reads:
// inventory is the noun, everything else is either a personal
// activity that touches inventory, or part of the community the
// user is in. Section labels locked in design review 2026-06-02
// and revised 2026-06-03 (third group: "More" → "Who I'm with",
// Practice Plans moved into "What I do" since it's a personal
// training tool parallel to Dry Fire).

const mainNav: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardOutlined /> },
];

// "What I own" — the gear LIFECYCLE, in flow order (revised 2026-09-03).
//
// This section used to be the noun alone (Inventory, Builds, Documents) with
// Purchases and Sales filed under "What I do" as verbs. That split was
// defensible but it put Inventory ABOVE the thing that creates it, so the menu
// read backwards against how gear actually moves. The owner's model is the
// pipeline: purchases lead to inventory, inventory leads to builds.
//
// So the order here IS the flow — gear comes in (Purchases), is what you have
// (Inventory), gets assembled (Builds), leaves (Sales) — with the paperwork
// that describes it last. "What I own" still reads true: this is the whole
// arc of owning something, including how it starts and ends.
const ownNav: NavItem[] = [
  { label: 'Purchases', path: '/purchases', icon: <ShoppingCartOutlined /> },
  { label: 'Inventory', path: '/inventory', icon: <InventoryOutlined /> },
  { label: 'Builds', path: '/inventory/builds', icon: <BuildOutlined /> },
  { label: 'Sales', path: '/sales', icon: <SellOutlined /> },
  { label: 'My Documents', path: '/documents', icon: <FolderOutlined /> },
];

// "What I do" — the personal verbs, now purely the training ones. Purchases and
// Sales moved into the lifecycle section above; what remains is what you do
// WITH the gear rather than how you acquire or dispose of it. Order is daily
// frequency.
const doNav: NavItem[] = [
  // M68d1 — ONE range item. "Matches" used to sit right below this as its own
  // nav entry, which read as two places for one thing: a match IS a range day
  // (M71a), so the standings are now a tab on this page rather than a sibling
  // destination. /competitions redirects to /range?view=standings, so existing
  // bookmarks land on the standings tab.
  { label: 'Live Fire', path: '/range', icon: <GpsFixedOutlined /> },
  { label: 'Dry Fire', path: '/dry-fire', icon: <AdjustOutlined /> },
  { label: 'Practice Plans', path: '/practice', icon: <SportsOutlined /> },
];

// "Who I'm with" — the community layer. Clubs the user belongs to
// (memberships), places they shoot (venues), and people they share
// inventory with (groups). Folded under a collapsible expander in the
// drawer (default collapsed): not load-bearing for a brand-new user,
// but a real semantic category once they have data, not a junk drawer.
// The app-wide maintainer Admin link and Settings live in the avatar
// menu (top-right), not the drawer.
//
// M71a — "Competitions" left this section for "What I do" (as "Matches"):
// a personal scoring record is a verb you do, not a community you're in.
const communityNav: NavItem[] = [
  { label: 'Memberships', path: '/membership', icon: <CardMembershipOutlined /> },
  { label: 'Venues', path: '/venues', icon: <PlaceOutlined /> },
  { label: 'Groups', path: '/groups', icon: <GroupsOutlined /> },
];

const allNavPaths: string[] = [
  ...mainNav, ...ownNav, ...doNav, ...communityNav,
].map((i) => i.path).filter((p): p is string => Boolean(p));

function selectedNavPath(currentPath: string): string | null {
  // `/` is the landing redirect; treat it as Dashboard for nav
  // highlighting (matches the Dashboard nav item's path).
  if (currentPath === '/' || currentPath === '/dashboard') return '/dashboard';
  let best: string | null = null;
  for (const p of allNavPaths) {
    if (p === '/') continue;
    if (currentPath === p || currentPath.startsWith(p + '/')) {
      if (!best || p.length > best.length) best = p;
    }
  }
  return best;
}

function NavSection({
  items,
  label,
  collapsed,
  onNavigate,
}: {
  items: NavItem[];
  label?: string;
  collapsed: boolean;
  // M34 — Called after every item click. Mobile uses this to dismiss
  // the temporary drawer so the user lands on the destination page
  // without having to swipe the drawer closed themselves.
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPath = selectedNavPath(location.pathname);

  return (
    <>
      {/* Section labels only make sense when there's room for them.
          In collapsed mode the icons flow together; the existing
          divider between Competition and Collaboration still provides
          a visual break. */}
      {label && !collapsed && (
        <Typography
          variant="overline"
          sx={{
            px: 2,
            pt: 1.25,
            pb: 0.25,
            display: 'block',
            color: 'text.secondary',
            fontSize: '0.65rem',
            lineHeight: 1.6,
          }}
        >
          {label}
        </Typography>
      )}
      <List disablePadding dense>
        {items.map((item) => {
          const selected = item.path ? item.path === selectedPath : false;
          const handleClick = () => {
            if (item.onClick) item.onClick();
            else if (item.path) navigate(item.path);
            onNavigate?.();
          };

          const button = (
            <ListItemButton
              selected={selected}
              onClick={handleClick}
              sx={{
                mx: 1,
                borderRadius: 1,
                mb: 0.125,
                // In collapsed mode, center the icon so it doesn't drift
                // toward the start edge with no label to balance it.
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 1.5,
                py: 0.5,
                minHeight: 36,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 36,
                  justifyContent: 'center',
                  color: selected ? 'primary.main' : 'text.secondary',
                  '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  secondary={item.secondary}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: selected ? 600 : 400 }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { fontFamily: 'monospace', lineHeight: 1.2, mt: '-2px' },
                  }}
                />
              )}
            </ListItemButton>
          );

          // Wrap with Tooltip in collapsed mode so users can hover to
          // see what each icon does. Tooltip on a ListItemButton needs
          // a wrapping element so the tooltip's ref attaches; the
          // ListItem already provides that.
          return (
            <ListItem key={item.path ?? item.label} disablePadding>
              {collapsed ? (
                <Tooltip
                  title={item.secondary ? `${item.label} · ${item.secondary}` : item.label}
                  placement="right"
                >
                  {button}
                </Tooltip>
              ) : (
                button
              )}
            </ListItem>
          );
        })}
      </List>
    </>
  );
}

export default function AppShell() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  // M69e — subscribe to taxonomy-override changes so a boot-load or admin edit
  // re-renders the route tree (Outlet → pages → chips re-read the resolver).
  useTaxonomyOverridesVersion();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, profile, signOut } = useAuth();
  const { open: openFeedback } = useFeedback();
  const { open: openInvite } = useInvite();
  // M46 — dot on the "What's new" item when the loaded bundle is
  // behind the latest published changelog row. Mirrors the Claude
  // Code / VS Code "update available" indicator.
  const { isStale } = useStaleBundle();
  const navigate = useNavigate();

  // Persisted collapse state. Only honored for the permanent (desktop)
  // drawer — the mobile temporary drawer always renders the full nav
  // when open. Stored in localStorage so a user's preference survives
  // reloads + new tabs.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  // M70c (E10) — drain the offline shot queue on mount and whenever the
  // browser comes back online. Single-flight + confirm-before-apply inside;
  // entries that keep failing stay queued for the pending-logs sheet.
  useEffect(() => {
    if (!user) return;
    const run = () => { replayPendingShots(user.id); };
    run();
    window.addEventListener('online', run);
    return () => window.removeEventListener('online', run);
  }, [user]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // No-op — private mode / disabled storage shouldn't break the app.
    }
  }, [collapsed]);

  // M54a — "Who I'm with" expander open state. Defaults closed for
  // everyone (community section isn't load-bearing for first-run).
  // The toggle affordance is hidden in collapsed mini-drawer mode
  // (no room) but the same state still gates communityNav rendering,
  // so a user who opened the section while expanded keeps it open
  // after they collapse the drawer.
  const [communityOpen, setCommunityOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COMMUNITY_OPEN_KEY) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COMMUNITY_OPEN_KEY, String(communityOpen));
    } catch {
      // No-op — private mode / disabled storage shouldn't break the app.
    }
  }, [communityOpen]);

  // The bottom section of the drawer: invite (opens the M45b modal),
  // feedback (opens the dialog), and What's new (links to /changelog
  // with the version as a subtitle). Defined inline so each entry can
  // capture its respective closure.
  const utilityNav: NavItem[] = [
    {
      label: 'Invite a friend',
      onClick: () => openInvite(),
      icon: <PersonAddOutlined />,
    },
    {
      label: 'Send feedback',
      onClick: () => openFeedback(),
      icon: <ChatBubbleOutlineIcon />,
    },
    {
      label: "What's new",
      path: '/changelog',
      icon: (
        // M46 — dot when a newer version exists in the changelog table
        // than the loaded bundle. `invisible` toggles visibility so
        // the Badge wrapper stays in the same DOM shape either way
        // (no layout shift). `overlap="circular"` tucks the dot into
        // the icon's top-right corner.
        <Badge
          variant="dot"
          color="error"
          invisible={!isStale}
          overlap="circular"
        >
          <NewReleasesOutlined />
        </Badge>
      ),
      secondary: `v${__APP_VERSION__}`,
    },
  ];

  const drawerWidth = collapsed && !isMobile ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  // M34 — Mobile: dismiss the temporary drawer after every nav click
  // so the user lands on the destination page without having to close
  // the drawer manually. No-op on desktop (permanent drawer stays).
  const handleNavigate = isMobile ? () => setMobileOpen(false) : undefined;

  function renderDrawerContent(c: boolean): React.ReactNode {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Top toolbar carries the brand AND (on desktop) the collapse
            toggle. This is the MUI mini-variant convention and the
            same pattern GitHub / Linear / Notion use — the toggle sits
            with the brand at the top so it doesn't compete with nav
            items and isn't hunted for at the bottom. In collapsed mode
            we drop the wordmark (no room) and just show the chevron
            centered; clicking it brings the brand back. */}
        <Toolbar
          variant="dense"
          sx={{
            gap: 1,
            minHeight: 56,
            justifyContent: c ? 'center' : 'flex-start',
            px: c ? 1 : 2,
          }}
        >
          {/* QuarterStack mark IS the drawer toggle.
              - Desktop: clicking collapses / expands the permanent rail.
              - Mobile: clicking dismisses the temporary drawer (paired
                with the outside/scrim click handler — same outcome,
                two reachable spots).
              In collapsed mini-rail mode there's no room for the
              wordmark; show the icon-only mark instead. */}
          <Tooltip
            title={
              isMobile
                ? 'Close navigation'
                : (c ? 'Expand navigation' : 'Collapse navigation')
            }
            placement="right"
          >
            <Box
              component="button"
              type="button"
              onClick={() => {
                if (isMobile) setMobileOpen(false);
                else setCollapsed((v) => !v);
              }}
              aria-label={
                isMobile
                  ? 'Close navigation'
                  : (c ? 'Expand navigation' : 'Collapse navigation')
              }
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                p: 0.5,
                m: -0.5,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
              }}
            >
              {c ? (
                <QSLogo variant="icon" height={28} />
              ) : (
                <QSLogo variant="full" height={28} />
              )}
            </Box>
          </Tooltip>
        </Toolbar>
        <Divider />
        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          <NavSection items={mainNav} collapsed={c} onNavigate={handleNavigate} />
          <NavSection items={ownNav} label="What I own" collapsed={c} onNavigate={handleNavigate} />
          <NavSection items={doNav} label="What I do" collapsed={c} onNavigate={handleNavigate} />
          {/* M54a — "Who I'm with" expander. Toggle only renders in
              expanded mode (no room for chevron + label in a 72px
              mini-rail); in collapsed mode the same communityOpen
              state still gates whether community icons render below,
              so the user's preference applies in both modes.
              Default state is false (closed) for everyone. */}
          {!c && (
            <ListItemButton
              onClick={() => setCommunityOpen((v) => !v)}
              aria-expanded={communityOpen}
              aria-controls="qs-community-nav"
              data-testid="qs-community-toggle"
              sx={{
                mx: 1,
                borderRadius: 1,
                mt: 0.5,
                mb: 0.125,
                px: 1.5,
                py: 0.5,
                minHeight: 36,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: 'text.secondary',
                  '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                }}
              >
                {communityOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemIcon>
              <ListItemText
                primary="Who I'm with"
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItemButton>
          )}
          <Collapse in={communityOpen} timeout="auto" unmountOnExit>
            <Box id="qs-community-nav">
              <NavSection items={communityNav} collapsed={c} onNavigate={handleNavigate} />
            </Box>
          </Collapse>
        </Box>
        {/* Bottom utility section — feedback + what's new. Separated
            from the main nav by a divider so it reads as
            meta-actions, not first-class destinations. */}
        <Divider />
        <Box sx={{ py: 1 }}>
          <NavSection items={utilityNav} collapsed={c} onNavigate={handleNavigate} />
        </Box>
        {/* M33 — Legal-link strip. Hidden in collapsed mode (no room
            for two text links + the chevron toggle on a 72px-wide
            rail; legal text was never visible there). Visible only
            when expanded so the user can reach Privacy + Terms
            without leaving the app. Sits below the utility nav, with
            no divider above so it visually trails as a foot-strip. */}
        {!c && (
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              justifyContent: 'center',
              alignItems: 'center',
              px: 2,
              py: 1,
              color: 'text.disabled',
            }}
          >
            <Link
              component={RouterLink}
              to="/privacy"
              variant="caption"
              color="inherit"
              underline="hover"
              onClick={handleNavigate}
            >
              Privacy
            </Link>
            <Typography variant="caption" aria-hidden>·</Typography>
            <Link
              component={RouterLink}
              to="/terms"
              variant="caption"
              color="inherit"
              underline="hover"
              onClick={handleNavigate}
            >
              Terms
            </Link>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH_MOBILE } }}
        >
          {/* Mobile drawer is always rendered in expanded form — it
              slides in over the page anyway, so labels are valuable
              and screen real estate while open is the user's focus. */}
          {renderDrawerContent(false)}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            // Smooth width transition when toggling collapse — without
            // this the layout snaps and reads as buggy.
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              borderRight: 1,
              borderColor: 'divider',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
            },
          }}
        >
          {renderDrawerContent(collapsed)}
        </Drawer>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Toolbar>
            {/* Hamburger is mobile-only. It opens the temporary drawer;
                closing happens via the QuarterStack logo inside the
                drawer, a nav item tap, or a scrim/outside click.
                Desktop has no hamburger — the logo in the drawer
                toggles collapse there. */}
            {isMobile && (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            {/* Persistent alpha indicator. Replaces the dismissible
                banner that used to live on the dashboard — keeping the
                stage visible at all times makes it harder for new
                testers to forget they're using pre-release software.
                Click opens the feedback dialog so a "saw a bug" reflex
                is one tap from any page.

                Visual: small uppercase pill in the GitHub / Linear /
                Notion "BETA" badge style — neutral colors so it reads
                as status, not as an alert. Pairs the stage with the
                version so testers can cite a build when reporting. */}
            <Tooltip title="QuarterStack is in closed alpha — expect bugs and rough edges. Tap to send feedback.">
              <Chip
                label={`ALPHA · v${__APP_VERSION__}`}
                size="small"
                variant="outlined"
                onClick={() => openFeedback()}
                sx={{
                  height: { xs: 22, sm: 26 },
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: 'text.secondary',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '& .MuiChip-label': { px: { xs: 0.875, sm: 1.25 } },
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    borderColor: 'text.secondary',
                  },
                }}
              />
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            {/* Feedback entry is icon-only now (was a labeled Button +
                icon, which read as cluttered next to the avatar). The
                drawer also has a "Send feedback" row and the alpha
                chip is clickable, so this stays as a familiar shortcut
                without doubling up the wordmark. */}
            <Tooltip title="Invite a friend">
              <IconButton
                onClick={() => openInvite()}
                color="inherit"
                aria-label="Invite a friend"
              >
                <PersonAddOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip title="Send feedback">
              <IconButton
                onClick={() => openFeedback()}
                color="inherit"
                aria-label="Send feedback"
              >
                <ChatBubbleOutlineIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                {profile?.display_name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              slotProps={{ paper: { sx: { minWidth: 220 } } }}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">{profile?.display_name || 'User'}</Typography>
              </MenuItem>
              <Divider />
              {profile?.is_admin && (
                <MenuItem onClick={() => { setAnchorEl(null); navigate('/admin'); }}>
                  <ListItemIcon><AdminPanelSettingsOutlined fontSize="small" /></ListItemIcon>
                  <ListItemText>Admin</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/settings'); }}>
                <ListItemIcon><SettingsOutlined fontSize="small" /></ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); signOut(); }}>
                <ListItemIcon><LogoutOutlined fontSize="small" /></ListItemIcon>
                <ListItemText>Sign Out</ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flex: 1, p: 3, bgcolor: 'background.default' }}>
          {/* M28 — Suspense lives inside AppShell so the drawer + topbar
              stay rendered during a lazy route fetch. The LinearProgress
              fallback fills the content area only. */}
          <Suspense fallback={<LinearProgress />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}
